import React, { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Stack, Typography, useMediaQuery, Button } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { AddOutlined, EditOutlined, Translate } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
interface StoryProps {
    open: boolean;
    name: string;
    storyContent: any;
    avatar?: string;
    handleClose: () => void;
    hanleOpenEditForm: () => void;
    handleSetFormStatus: (status: string) => void;
    role: string;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(4, 5, 5, 5),
        color: '#3e3124',
        fontFamily: '"Lora", "Georgia", serif',
        lineHeight: 1.9,
        fontSize: '1.1rem',
        backgroundColor: '#fbf7f0',
        '& img': {
            maxWidth: '100%',
            borderRadius: '4px',
            border: '2px solid #e0d5c1',
            padding: '4px',
            marginTop: '16px',
            marginBottom: '16px',
        },
        '& p': {
            marginBottom: '1.2em'
        },
        '& p:first-of-type::first-letter': {
            float: 'left',
            fontSize: '3.8rem',
            lineHeight: 0.8,
            paddingTop: '8px',
            paddingRight: '8px',
            paddingLeft: '3px',
            fontFamily: '"Playfair Display", "Times New Roman", serif',
            color: '#8b7355',
            fontWeight: 'bold'
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3),
            fontSize: '1.05rem',
        }
    },
    '& .MuiDialog-paper': {
        minWidth: '550px',
        borderRadius: '8px',
        border: '1px solid #d4c4a8',
        backgroundColor: '#fbf7f0',
        boxShadow: '0 24px 60px rgba(50, 40, 30, 0.25)',
        backgroundImage: 'linear-gradient(to right, rgba(139, 115, 85, 0.08) 0%, rgba(139, 115, 85, 0) 4%, rgba(139, 115, 85, 0) 96%, rgba(139, 115, 85, 0.08) 100%)',
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            margin: 0,
            borderRadius: 0,
        },
    },
}));

export default function StoryDialog({ open, name, avatar, storyContent, role, handleClose, hanleOpenEditForm, handleSetFormStatus }: StoryProps) {
    const [openFullScreen, setOpenFullScreen] = useState(false);
    const [translatedContent, setTranslatedContent] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { language } = useLanguage();
    
    const handleToggleFullScreen = () => {
        setOpenFullScreen(!openFullScreen);
    };

    const translateContent = async (text: string) => {
        try {
            const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${language}&dt=t&q=${encodeURIComponent(text)}`);
            const data = await response.json();
            const translatedText = data[0].map((item: any) => item[0]).join('');
            setTranslatedContent(translatedText);
        } catch (error) {
            console.error('Translation error:', error);
            setTranslatedContent(text); 
        } 
    };

    useEffect(() => {
        if (storyContent && language) {
            translateContent(storyContent);
        }
    }, [storyContent, language]);


    return (
        <BootstrapDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullScreen={isMobile || openFullScreen}
            maxWidth={'sm'}
        >
            <Box sx={{ position: 'relative', background: 'transparent', pb: 2, pt: 5, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Stack direction="row" sx={{ position: 'absolute', top: 16, right: 16 }} spacing={1}>
                    <IconButton
                        aria-label="fullscreen"
                        onClick={handleToggleFullScreen}
                        size='small'
                        sx={{ 
                            color: '#8b7355', backgroundColor: 'rgba(139, 115, 85, 0.05)', 
                            '&:hover': { backgroundColor: 'rgba(139, 115, 85, 0.1)' },
                            display: { xs: 'none', sm: 'flex' },
                            border: '1px solid rgba(139, 115, 85, 0.2)'
                        }}
                    >
                        {openFullScreen ? <CloseFullscreenIcon fontSize='small'/> : <FullscreenIcon fontSize='small'/>}
                    </IconButton>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        size='small'
                        sx={{ 
                            color: '#8b7355', backgroundColor: 'rgba(139, 115, 85, 0.05)', 
                            '&:hover': { backgroundColor: 'rgba(139, 115, 85, 0.1)' },
                            border: '1px solid rgba(139, 115, 85, 0.2)'
                        }}
                    >
                        <CloseIcon fontSize='small'/>
                    </IconButton>
                </Stack>

                {avatar && (
                    <Avatar 
                        src={avatar} 
                        alt={name} 
                        sx={{ 
                            width: 140, 
                            height: 140, 
                            border: '4px double #d4c4a8', 
                            backgroundColor: '#fbf7f0',
                            padding: '4px',
                            boxShadow: '0 8px 24px rgba(62, 49, 36, 0.15)',
                            mb: 3
                        }} 
                    />
                )}
                
                <Typography sx={{ 
                    fontWeight: 700, 
                    fontSize: '2rem', 
                    color: '#3e3124',
                    fontFamily: '"Playfair Display", "Times New Roman", serif',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                    mb: role === 'admin' ? 3 : 2,
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        display: 'block',
                        width: '60px',
                        height: '2px',
                        backgroundColor: '#8b7355',
                        margin: '20px auto 0',
                        opacity: 0.5
                    }
                }}>
                   {language === 'en' ? `${name}'s Memoir` : 'Hồi ký của ' + name} 
                </Typography>

                {role === 'admin' && (
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
                        <Button 
                            startIcon={<EditOutlined />}
                            onClick={() => {
                                hanleOpenEditForm()
                                handleSetFormStatus('edit')
                            }}
                            size="small"
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                fontFamily: 'Inter',
                                borderRadius: '4px',
                                color: '#6b8e51', 
                                backgroundColor: 'rgba(107, 142, 81, 0.05)',
                                border: '1px solid rgba(107, 142, 81, 0.2)',
                                px: 2,
                                py: 0.5,
                                '&:hover': { backgroundColor: 'rgba(107, 142, 81, 0.1)' }
                            }}
                        >
                            {language === 'en' ? 'Edit Memoir' : 'Sửa hồi ký'}
                        </Button>
                        <Button 
                            startIcon={<AddOutlined />}
                            onClick={() => {
                                handleSetFormStatus('add')
                                hanleOpenEditForm()
                            }}
                            size="small"
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                fontFamily: 'Inter',
                                borderRadius: '4px',
                                color: '#c96a52', 
                                backgroundColor: 'rgba(201, 106, 82, 0.05)',
                                border: '1px solid rgba(201, 106, 82, 0.2)',
                                px: 2,
                                py: 0.5,
                                '&:hover': { backgroundColor: 'rgba(201, 106, 82, 0.1)' }
                            }}
                        >
                            {language === 'en' ? 'Add Member' : 'Thêm người'}
                        </Button>
                    </Stack>
                )}
            </Box>
            <DialogContent>
                {(!translatedContent && !storyContent) ? (
                    <Typography sx={{ textAlign: 'center', color: '#8b7355', fontStyle: 'italic', fontFamily: 'Inter', mt: 4, mb: 4 }}>
                        {language === 'en' ? 'No memoir written yet.' : 'Chưa có dòng hồi ký nào.'}
                    </Typography>
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: translatedContent || storyContent }}></div>
                )}
            </DialogContent>
        </BootstrapDialog>
    );
}
